import { useCallback, useEffect, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';

import {
  markAllNotificationsRead,
  markNotificationRead,
  subscribeToNotifications,
  subscribeToUnreadNotificationCount,
  type AppNotification,
} from './notification-service';

type NotificationsState = {
  error: Error | null;
  loading: boolean;
  notifications: AppNotification[];
};

const NOTIFICATION_PAGE_SIZE = 50;

export function useNotifications(
  uid: string | undefined,
  limitCount = NOTIFICATION_PAGE_SIZE,
  enabled = true,
) {
  const [effectiveLimit, setEffectiveLimit] = useState(limitCount);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const notificationsRef = useRef<AppNotification[]>([]);
  const uidRef = useRef<string | undefined>(undefined);
  const [state, setState] = useState<NotificationsState>({
    error: null,
    loading: Boolean(uid),
    notifications: [],
  });

  useEffect(() => {
    setEffectiveLimit(limitCount);
  }, [limitCount]);

  useEffect(() => {
    if (uidRef.current !== uid) {
      uidRef.current = uid;
      notificationsRef.current = [];
    }

    if (!uid) {
      notificationsRef.current = [];
      setLoadingMore(false);
      setRefreshing(false);
      setState({ error: null, loading: false, notifications: [] });
      return undefined;
    }

    if (!enabled) {
      setLoadingMore(false);
      setRefreshing(false);
      setState((currentState) => ({
        ...currentState,
        error: null,
        loading: false,
      }));
      return undefined;
    }

    setState((currentState) => ({
      ...currentState,
      error: null,
      loading: notificationsRef.current.length === 0,
      notifications:
        notificationsRef.current.length === 0 ? [] : currentState.notifications,
    }));

    return subscribeToNotifications({
      limitCount: effectiveLimit,
      uid,
      onError: (error) => {
        setState((currentState) => ({
          ...currentState,
          error,
          loading: false,
        }));
        setLoadingMore(false);
        setRefreshing(false);
      },
      onNext: (notifications) => {
        notificationsRef.current = notifications;
        setState({ error: null, loading: false, notifications });
        setLoadingMore(false);
        setRefreshing(false);
      },
    });
  }, [effectiveLimit, enabled, refreshNonce, uid]);

  const hasMore = state.notifications.length >= effectiveLimit;

  const loadMore = useCallback(() => {
    if (state.loading || loadingMore || refreshing || !hasMore) {
      return;
    }

    setLoadingMore(true);
    setEffectiveLimit((currentLimit) => currentLimit + NOTIFICATION_PAGE_SIZE);
  }, [hasMore, loadingMore, refreshing, state.loading]);

  const refresh = useCallback(() => {
    if (!uid) {
      return;
    }

    setRefreshing(true);
    setRefreshNonce((currentNonce) => currentNonce + 1);
  }, [uid]);

  return {
    ...state,
    hasMore,
    loadMore,
    loadingMore,
    refresh,
    refreshing,
  };
}

export function useUnreadNotificationCount(uid: string | undefined) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!uid) {
      setCount(0);
      return undefined;
    }

    return subscribeToUnreadNotificationCount({
      uid,
      onError: () => {
        setCount(0);
      },
      onNext: setCount,
    });
  }, [uid]);

  return count;
}

export function useMarkNotificationRead(uid: string | undefined) {
  return useMutation({
    mutationFn: async (notificationId: string) => {
      if (!uid) {
        throw new Error('You must be signed in to update notifications.');
      }

      await markNotificationRead({ notificationId, uid });
    },
  });
}

export function useMarkAllNotificationsRead(uid: string | undefined) {
  return useMutation({
    mutationFn: async () => {
      if (!uid) {
        throw new Error('You must be signed in to update notifications.');
      }

      await markAllNotificationsRead(uid);
    },
  });
}
